import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import HierarchicalOpalGrid from '../DenaliDataPipeline/report/HierarchicalOpalGrid'
import Select from '../../molecules/Select'
import SydButton from '../../commonDesign/Button'
import FlexColumn from '../../molecules/FlexColumn'
import { localStorageHelper } from '../../../utils/localStorageHelper'
import { useGetBlueprintLatchDates } from '../../../api/denali'
import useConsumeQueryParam from '../../../hooks/useConsumeQueryParam'
import useBlueprintColumnDefs, { mapDefaultSort } from './hooks/useBlueprintColumnDefs'
import useBlueprintLazyTreeData from './hooks/useBlueprintLazyTreeData'
import GridColumnConfigModal from './GridColumnConfigModal'
import DynamicFilterModal from './DynamicFilterModal'
import BlueprintExportButton from './BlueprintExportButton'
dayjs.extend(utc)

function buildPredicateOptions (subordinatePredicates, description) {
  const options = subordinatePredicates.map(p => ({
    label: p.description || p.code,
    value: p.code
  }))
  options.splice(0, 0, { label: description || 'All', value: null })
  return options
}

function loadSavedColumns (storageKey, blueprintColumns) {
  if (!storageKey) return null
  const saved = localStorageHelper.tryLoad(storageKey)
  if (!Array.isArray(saved) || saved.length === 0) return null
  const blueprintNames = new Set(blueprintColumns.map(c => c.name))
  const savedNames = new Set(saved.map(c => c.name))
  if (blueprintNames.size !== savedNames.size) return null
  for (const name of blueprintNames) {
    if (!savedNames.has(name)) return null
  }
  return saved
}

function getSubRows (row) {
  return row.subRows
}

/**
 * Blueprint-driven hierarchical grid with lazy-loading tree data.
 * Supports column configuration, dynamic filtering, export,
 * latch dates, and subordinate predicates.
 */
function BlueprintHierarchicalGrid ({
  blueprintCode,
  config,
  displayName,
  description,
  subordinatePredicates,
  latch,
  purposeColumns,
  parameters,
  filterParam = 'filterCode'
}) {
  const {
    hierarchy,
    defaultSort: rawDefaultSort,
    localStorageKey,
    enableHierarchicalFilter
  } = config

  const primaryPurposeCode = hierarchy?.levels?.[0]?.purposeCode
  const hasPredicates = subordinatePredicates?.length > 0

  const storageKey = localStorageKey ? `${localStorageKey}.columns` : null

  const [userColumnPrefs, setUserColumnPrefs] = useState(() => {
    return loadSavedColumns(storageKey, config.columns)
  })

  const mergedColumns = useMemo(() => {
    if (!userColumnPrefs) return config.columns
    const blueprintMap = new Map(config.columns.map(c => [c.name, c]))
    return userColumnPrefs.map(saved => {
      const fresh = blueprintMap.get(saved.name)
      if (!fresh) return saved
      return { ...fresh, hidden: saved.hidden }
    })
  }, [config.columns, userColumnPrefs])

  const [configOpen, setConfigOpen] = useState(false)
  const [filterOpen, setFilterOpen] = useState(false)
  const [dynamicFilters, setDynamicFilters] = useState([])
  const [globalFilter, setGlobalFilter] = useState('')

  const filterableColumns = useMemo(() => {
    const purposeMap = new Map((purposeColumns || []).map(c => [c.name, c]))
    return config.columns
      .filter(c => c.filterable && !c.exclude)
      .map(c => {
        const purpose = purposeMap.get(c.name)
        return {
          ...c,
          dataType: purpose?.dataType || 'string',
          faceted: purpose?.faceted || false
        }
      })
  }, [config.columns, purposeColumns])

  const handleFiltersApply = useCallback((filters) => {
    setDynamicFilters(filters)
    setPageState(prev => ({ ...prev, pageIndex: 0 }))
  }, [])

  const handleColumnConfigSave = useCallback((updatedColumns) => {
    setUserColumnPrefs(updatedColumns)
    if (storageKey) localStorageHelper.store(storageKey, updatedColumns)
  }, [storageKey])

  const [sortState, setSortState] = useState(() => mapDefaultSort(rawDefaultSort))
  const [pageState, setPageState] = useState({ pageIndex: 0, pageSize: 100 })

  const handleSortChange = useCallback((updater) => {
    setSortState(updater)
    setPageState(prev => ({ ...prev, pageIndex: 0 }))
  }, [])

  const initialPredicateCode = useConsumeQueryParam(filterParam)
  const [selectedPredicate, setSelectedPredicate] = useState(() => {
    if (!initialPredicateCode || !hasPredicates) return null
    const match = subordinatePredicates.find(p => p.code === initialPredicateCode)
    return match ? match.code : null
  })

  const predicateOptions = useMemo(() => {
    if (!hasPredicates) return []
    return buildPredicateOptions(subordinatePredicates, description)
  }, [subordinatePredicates, description, hasPredicates])

  // Latch date support
  const hasLatch = !!latch
  const latchMode = config.latchMode || 'selectable'
  const { data: latchDatesData } = useGetBlueprintLatchDates(
    blueprintCode,
    primaryPurposeCode,
    { enabled: hasLatch, limit: latchMode === 'latest' ? 1 : (latch?.limit ?? 100) }
  )
  const latchDates = useMemo(() => latchDatesData?.dates ?? [], [latchDatesData])
  const [selectedDate, setSelectedDate] = useState(null)
  const effectiveDate = selectedDate ?? latchDates[0] ?? null

  const latchDateOptions = useMemo(() => {
    if (!latchDates.length) return []
    return latchDates.map(d => ({
      label: dayjs.utc(d).format('[As of] MMMM D, YYYY'),
      value: d
    }))
  }, [latchDates])

  const effectiveParameters = useMemo(() => {
    if (!hasLatch || !effectiveDate) return parameters
    return { ...parameters, [latch.parameterName]: effectiveDate }
  }, [parameters, hasLatch, latch, effectiveDate])

  const latchParams = useMemo(() => {
    if (!hasLatch || !effectiveDate) return {}
    return { [latch.parameterName]: effectiveDate }
  }, [hasLatch, latch, effectiveDate])

  const sortBy = sortState?.[0]?.id ?? null
  const sortDirection = sortState?.[0]?.desc ? 'desc' : 'asc'
  const activePredCodes = useMemo(() => {
    return selectedPredicate ? [selectedPredicate] : []
  }, [selectedPredicate])

  const {
    treeData, metadata, totalLevels, isLoading,
    expandState, setExpandState,
    onLevelExpand
  } = useBlueprintLazyTreeData(
    blueprintCode,
    hierarchy,
    {
      parameters: effectiveParameters,
      latchParams,
      page: pageState.pageIndex,
      pageSize: pageState.pageSize,
      sortBy,
      sortDirection,
      filters: dynamicFilters,
      subordinatePredicates: activePredCodes
    }
  )

  const getRowCanExpand = useCallback((row) => {
    const levelIndex = row.original?._levelIndex ?? 0
    return levelIndex < totalLevels - 1
  }, [totalLevels])

  // Wrap expand state changes to trigger lazy fetching for unfetched rows.
  // When TanStack Table toggles a row to expanded, we intercept it here
  // and fire onLevelExpand which fetches child data if not yet loaded.
  const handleExpandStateChange = useCallback((updater) => {
    const nextState = typeof updater === 'function' ? updater(expandState) : updater
    const findOriginalByPath = (nodes, pathParts) => {
      const idx = Number(pathParts[0])
      const node = nodes?.[idx]
      if (!node) return null
      if (pathParts.length === 1) return node
      return findOriginalByPath(node.subRows, pathParts.slice(1))
    }
    for (const [rowId, isExpanded] of Object.entries(nextState)) {
      if (isExpanded && !expandState[rowId]) {
        const original = findOriginalByPath(treeData, rowId.split('.'))
        if (original && !original._subRowsFetched && !original._subRowsFetching) {
          onLevelExpand({ id: rowId, original })
        }
      }
    }
    setExpandState(nextState)
  }, [expandState, treeData, onLevelExpand, setExpandState])

  const { columns, includeFooter } = useBlueprintColumnDefs(mergedColumns, [], [])

  const visibleColumns = useMemo(() => {
    return columns.filter(col => col.defaultShown ?? true)
  }, [columns])

  return (
    <FlexColumn className='gap-xs'>
      <div className='flex flex-row justify-between items-end'>
        <div className='flex flex-col gap-xs'>
          {displayName
            ? <div className='opal-heading opal-heading-2 font-size-lg'>{displayName}</div>
            : null}
          <div className='flex flex-row gap-md items-center font-size-sm'>
            {hasPredicates
              ? (
                <Select
                  value={selectedPredicate}
                  onChange={setSelectedPredicate}
                  options={predicateOptions}
                  placeholder={description || 'All'}
                />
              )
              : null}
            {hasLatch && latchMode === 'latest' && effectiveDate
              ? (
                <span className='font-size-sm' style={{ color: 'var(--text-color-subtle)' }}>
                  {dayjs.utc(effectiveDate).format('[As of] MMMM D, YYYY')}
                </span>
              )
              : null}
            {hasLatch && latchMode !== 'latest' && latchDateOptions.length > 0
              ? (
                <Select
                  value={effectiveDate}
                  onChange={setSelectedDate}
                  options={latchDateOptions}
                  placeholder='Select date'
                />
              )
              : null}
          </div>
        </div>
        <div className='flex flex-row gap-sm'>
          <BlueprintExportButton
            blueprintCode={blueprintCode}
            config={config}
            purposeCode={primaryPurposeCode}
            parameters={effectiveParameters}
            filters={dynamicFilters}
            subordinatePredicates={activePredCodes}
            sortBy={sortBy}
            sortDirection={sortDirection}
          />
          <SydButton
            onClick={() => setFilterOpen(true)}
            variant='outline'
            size='xs'
            icon='filters'
          >
            {dynamicFilters.length > 0
              ? `${dynamicFilters.length} Filter${dynamicFilters.length === 1 ? '' : 's'} applied`
              : 'Filters'}
          </SydButton>
          <SydButton
            onClick={() => setConfigOpen(true)}
            variant='outline'
            size='xs'
            icon='settings'
          >
            Configure
          </SydButton>
        </div>
      </div>
      <HierarchicalOpalGrid
        data={treeData}
        columns={visibleColumns}
        isFetching={isLoading}
        pageState={pageState}
        onPageChange={setPageState}
        expandState={expandState}
        onExpandStateChange={handleExpandStateChange}
        sortState={sortState}
        onSortChange={handleSortChange}
        getSubRows={getSubRows}
        getRowCanExpand={getRowCanExpand}
        manualPagination
        manualSorting
        totalRows={metadata?.totalRowCount}
        pageSizeOptions={[50, 100, 250, 500]}
        includeFooter={includeFooter}
        enableFiltering={!!enableHierarchicalFilter}
        globalFilter={enableHierarchicalFilter ? globalFilter : undefined}
        onGlobalFilterChange={enableHierarchicalFilter ? setGlobalFilter : undefined}
      />
      <GridColumnConfigModal
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        columns={mergedColumns}
        defaultColumns={config.columns}
        onSave={handleColumnConfigSave}
      />
      {primaryPurposeCode
        ? (
          <DynamicFilterModal
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
            columns={filterableColumns}
            blueprintCode={blueprintCode}
            purposeCode={primaryPurposeCode}
            activeFilters={dynamicFilters}
            onApply={handleFiltersApply}
            parameters={effectiveParameters}
          />
        )
        : null}
    </FlexColumn>
  )
}

BlueprintHierarchicalGrid.propTypes = {
  blueprintCode: PropTypes.string.isRequired,
  config: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      hidden: PropTypes.bool,
      exclude: PropTypes.bool,
      sortable: PropTypes.bool,
      filterable: PropTypes.bool,
      formatString: PropTypes.string
    })),
    hierarchy: PropTypes.shape({
      levels: PropTypes.arrayOf(PropTypes.shape({
        purposeCode: PropTypes.string.isRequired,
        paramMapping: PropTypes.object
      })).isRequired,
      initialFetchDepth: PropTypes.number,
      defaultExpandDepth: PropTypes.number
    }).isRequired,
    defaultSort: PropTypes.arrayOf(PropTypes.shape({
      field: PropTypes.string,
      dir: PropTypes.string
    })),
    localStorageKey: PropTypes.string,
    variationGroups: PropTypes.arrayOf(PropTypes.string),
    latchMode: PropTypes.oneOf(['selectable', 'latest']),
    enableHierarchicalFilter: PropTypes.bool,
    export: PropTypes.shape({
      enabled: PropTypes.bool,
      formats: PropTypes.arrayOf(PropTypes.string),
      defaultFileName: PropTypes.string,
      flattenOnExport: PropTypes.bool
    })
  }).isRequired,
  displayName: PropTypes.string,
  description: PropTypes.string,
  subordinatePredicates: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string.isRequired,
    description: PropTypes.string,
    expression: PropTypes.string
  })),
  latch: PropTypes.shape({
    parameterName: PropTypes.string.isRequired,
    dateColumn: PropTypes.string.isRequired,
    dateTable: PropTypes.string.isRequired,
    limit: PropTypes.number
  }),
  purposeColumns: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    dataType: PropTypes.string,
    faceted: PropTypes.bool
  })),
  parameters: PropTypes.object,
  filterParam: PropTypes.string
}

BlueprintHierarchicalGrid.defaultProps = {
  parameters: {},
  subordinatePredicates: [],
  purposeColumns: []
}

export default BlueprintHierarchicalGrid
